import { itemsApi } from "#/api/itemsApi";
import ActionButton from "#/components/buttons/ActionButton";
import InventoryMap from "#/components/ui/InventoryMap";
import { type MapDataItem } from "#/types/items";
import type { MapCountItem } from "#/types/stockAlert";
import { sanitize } from "#/utils/StringUtils";
import { Skeleton, Title, UnstyledButton } from "@mantine/core";
import { IconArrowRight, IconMapPinFilled } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import StockAlertPinCard from "./StockAlertPinCard";

/**
 * Module scope so the object identity stays stable — GoogleMap re-applies its
 * options whenever this prop changes.
 */
const MAP_OPTIONS: google.maps.MapOptions = {
  zoomControl: true,
  fullscreenControl: false,
  keyboardShortcuts: false,
};

const StocksAlert = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedMapItem, setSelectedMapItem] = useState<MapCountItem>();
  const [allItems, setAllItems] = useState<MapDataItem[]>([]);

  const { data = [], isFetching } = useQuery({
    queryKey: ["items-map-data"],
    queryFn: async () => {
      const response = await itemsApi.getMapData();

      if (!response.data) {
        throw new Error("Failed to fetch map data");
      }

      const colorMap = new Map<string, string>();
      const usedColors = new Set<string>();

      const getNextAvailableColor = () => {
        const mantineColors = [
          "#228be6", // blue
          "#20c997", // teal
          "#845ef7", // violet
          "#e64980", // pink
          "#fab005", // yellow
          "#40c057", // green
          "#fa5252", // red
          "#15aabf", // cyan
          "#fd7e14", // orange
          "#1c7ed6", // darker blue
          "#82c91e", // lime
          "#f76707", // deep orange
        ];
        for (const color of mantineColors) {
          if (!usedColors.has(color)) {
            usedColors.add(color);
            return color;
          }
        }
        const hue = Math.floor(Math.random() * 360);
        return `hsl(${hue}, 70%, 50%)`;
      };

      const mapCountItems: MapCountItem[] = Object.entries(
        response.data?.counts || {}
      ).map(([key, count]) => {
        const items =
          response.data.items?.filter((item) => item.status === key) ?? [];
        const color = colorMap.get(key) ?? getNextAvailableColor();
        if (!colorMap.has(key)) {
          colorMap.set(key, color);
        }

        return {
          items,
          color,
          count,
          label: sanitize(key),
          status: key,
        };
      });

      setAllItems(response.data.items);

      // setSelectedMapItem(mapCountItems[0]);
      return mapCountItems;
    },
    enabled: true,
    staleTime: 1000 * 60,
  });

  return (
    <div className="w-full flex flex-col gap-2">
      <Title order={4}>{t("dashboard.stockAlerts.title")}</Title>
      {isFetching ? (
        <div className="w-full h-76 flex ">
          <Skeleton radius={"md"} />
        </div>
      ) : (
        <div className="w-full flex flex-col gap-4">
          <div className="w-full flex flex-wrap gap-2">
            {/* Only statuses that actually have items; zero cards are noise. */}
            {data?.filter((item) => Number(item.count) > 0).map((item) => (
              <div
                key={item.status}
                className={`flex items-center gap-2 pl-4 pr-2 py-1 rounded-md
                  hover:bg-client-color/50 ${selectedMapItem?.status === item.status ? "bg-client-color/30" : "bg-client-color/10"}
                  transition-all duration-200`}
              >
                <UnstyledButton
                  className="flex-1 flex items-center gap-2 py-1 cursor-pointer"
                  onClick={() => {
                    if (selectedMapItem?.status === item.status) {
                      setSelectedMapItem(undefined);
                    } else {
                      setSelectedMapItem(item);
                    }
                  }}
                >
                  <IconMapPinFilled color={item.color} size={16} />
                  <span className="text-sm font-medium capitalize whitespace-nowrap">
                    {item.label}
                  </span>
                  <span className="text-lg font-bold ml-auto pl-4">
                    {item.count ?? "--"}
                  </span>
                </UnstyledButton>
                <ActionButton
                  icon={IconArrowRight}
                  size="sm"
                  variant="subtle"
                  aria-label={t("items.filters.status")}
                  onClick={() =>
                    navigate(
                      `/inventory/items?status=${encodeURIComponent(item.status)}`
                    )
                  }
                />
              </div>
            ))}
          </div>
          <div className="w-full h-[450px] flex rounded-lg overflow-hidden">
            <InventoryMap
              markers={(selectedMapItem ? selectedMapItem.items : allItems).map((item) => {
                const color = data.find((i) => i.status === item.status)?.color ?? "";
                return {
                  longitude: item.longitude ?? item.site?.longitude,
                  latitude: item.latitude ?? item.site?.latitude,
                  color,
                  infoWindowContent: () => <StockAlertPinCard item={item} color={color} />,
                };
              })}
              zoom={11}
              additionalComponent={null}
              enableClustering
              options={MAP_OPTIONS}
            />
          </div>


        </div>
      )}
    </div>
  );
};

export default StocksAlert;
